import {
  Course,
  Exercise,
  ExercisePayload,
  ExerciseWithDetails,
  ExerciseWithDetailsAndStructure,
  Module,
} from "@nicsmith/shared_types";
import { baseApi } from "./api";

export const exerciseApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Fetch all exercises with optional filters
    getAllExercises: builder.query<
      Exercise[] | ExerciseWithDetails[] | ExerciseWithDetailsAndStructure[],
      { includeStructure?: boolean; includeDetails?: boolean } & {
        [key: string]: string | number | boolean;
      }
    >({
      query: (filters) => ({
        url: "/exercises",
        params: filters || undefined,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Exercise" as const, id })),
              { type: "Exercise" as const, id: "LIST" },
            ]
          : [{ type: "Exercise" as const, id: "LIST" }],
      transformResponse: (
        response:
          | Exercise[]
          | ExerciseWithDetails[]
          | ExerciseWithDetailsAndStructure[],
        meta,
        arg
      ) => {
        if (arg.includeStructure) {
          return response as ExerciseWithDetailsAndStructure[];
        }
        if (arg.includeDetails) {
          return response as ExerciseWithDetails[];
        }
        return response as Exercise[];
      },
    }),

    getExercisesByCategoryId: builder.query<
      Exercise[] | ExerciseWithDetails[] | ExerciseWithDetailsAndStructure[],
      {
        categoryId: number;
        includeStructure?: boolean;
        includeDetails?: boolean;
        directOnly?: boolean;
      }
    >({
      query: ({ categoryId, includeStructure, includeDetails, directOnly }) => ({
        url: `/exercises/category/${categoryId}`,
        params: {
          includeStructure,
          includeDetails,
          directOnly,
        },
      }),
      providesTags: (result, error, { categoryId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Exercise" as const, id })),
              { type: "Exercise" as const, id: `category-${categoryId}` },
            ]
          : [{ type: "Exercise" as const, id: `category-${categoryId}` }],
      transformResponse: (
        response:
          | Exercise[]
          | ExerciseWithDetails[]
          | ExerciseWithDetailsAndStructure[],
        meta,
        arg
      ) => {
        if (arg.includeStructure) {
          return response as ExerciseWithDetailsAndStructure[];
        }
        if (arg.includeDetails) {
          return response as ExerciseWithDetails[];
        }
        return response as Exercise[];
      },
    }),

    getExerciseById: builder.query<
      Exercise | ExerciseWithDetails | ExerciseWithDetailsAndStructure,
      { id: string; includeDetails?: boolean; includeStructure?: boolean }
    >({
      query: ({ id, includeDetails, includeStructure }) => ({
        url: `/exercises/${id}`,
        params: {
          includeDetails,
          includeStructure,
        },
      }),
      providesTags: (result, error, { id }) => [
        { type: "Exercise" as const, id },
      ],
      transformResponse: (
        response:
          | Exercise
          | ExerciseWithDetails
          | ExerciseWithDetailsAndStructure,
        meta,
        arg
      ) => {
        if (arg.includeStructure) {
          return response as ExerciseWithDetailsAndStructure;
        }
        if (arg.includeDetails) {
          return response as ExerciseWithDetails;
        }
        return response as Exercise;
      },
    }),

    addExercise: builder.mutation<
      ExercisePayload,
      { exercise: ExercisePayload }
    >({
      query: ({ exercise }) => ({
        url: "/exercises",
        method: "POST",
        body: { ...exercise },
      }),
      invalidatesTags: [{ type: "Exercise", id: "LIST" }, "Courses"],
    }),

    updateExercise: builder.mutation<
      ExerciseWithDetails,
      { id: string; data: ExercisePayload }
    >({
      query: ({ id, data }) => ({
        url: `/exercises/${id}`,
        method: "PUT",
        body: { ...data },
      }),
      // Invalidate both string id (used by getExerciseById query args) and
      // numeric id (used by getAllExercises/resolveExercisePath providesTags
      // which extract id from the API response as a number).
      invalidatesTags: (result, error, { id }) => [
        { type: "Exercise", id },
        { type: "Exercise", id: Number(id) },
        { type: "Exercise", id: "LIST" },
        "Courses",
      ],
    }),

    deleteExercise: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/exercises/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Exercise", id },
        { type: "Exercise", id: Number(id) },
        { type: "Exercise", id: "LIST" },
        { type: "Module", id: "LIST" },
        "Courses",
      ],
    }),

    // Toggle exercise publication status
    toggleExercisePublication: builder.mutation<Exercise, { id: string; is_published: boolean }>({
      query: ({ id, is_published }) => ({
        url: `/exercises/${id}`,
        method: "PUT",
        body: { is_published },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Exercise", id },
        { type: "Exercise", id: Number(id) },
        { type: "Exercise", id: "LIST" },
        "Courses",
      ],
    }),

    // Resolve course/module/exercise from slugs in one call
    resolveExercisePath: builder.query<
      {
        course: Course;
        module: Module;
        exercise: ExerciseWithDetails | ExerciseWithDetailsAndStructure;
      },
      { courseSlug: string; moduleSlug: string; exerciseParam: string; includeStructure?: boolean }
    >({
      query: ({ courseSlug, moduleSlug, exerciseParam, includeStructure }) => ({
        url: `/resolve/course/${courseSlug}/module/${moduleSlug}/exercise/${exerciseParam}`,
        params: { includeStructure },
      }),
      providesTags: (result) =>
        result
          ? [{ type: "Exercise" as const, id: result.exercise.id }]
          : [],
    }),

    // Reorder exercises within a module
    reorderExercises: builder.mutation<
      { message: string },
      { moduleId: number; orderedExerciseIds: number[] }
    >({
      query: ({ moduleId, orderedExerciseIds }) => ({
        url: "/exercises/reorder",
        method: "POST",
        body: { module_id: moduleId, ordered_exercise_ids: orderedExerciseIds },
      }),
      // Invalidate ALL Exercise queries (list, byId, resolveExercisePath)
      // so stale position-based caches are busted after reorder
      invalidatesTags: ["Exercise"],
    }),

    // Browse all published exercises (no category/module filter)
    browseAllExercises: builder.query<ExerciseWithDetails[], void>({
      query: () => "/exercises/browse",
      providesTags: [{ type: "Exercise" as const, id: "BROWSE" }],
    }),

    // Generate exercise audio via TTS
    generateExerciseAudio: builder.mutation<
      { message: string; url: string; key: string },
      { text: string }
    >({
      query: ({ text }) => ({
        url: "/exercises/generate-audio",
        method: "POST",
        body: { text },
      }),
    }),
  }),
  overrideExisting: true,
});

// Export hooks for usage in components
export const {
  useGetAllExercisesQuery,
  useLazyGetAllExercisesQuery,
  useGetExercisesByCategoryIdQuery,
  useLazyGetExercisesByCategoryIdQuery,
  useGetExerciseByIdQuery,
  useAddExerciseMutation,
  useUpdateExerciseMutation,
  useDeleteExerciseMutation,
  useToggleExercisePublicationMutation,
  useReorderExercisesMutation,
  useBrowseAllExercisesQuery,
  useGenerateExerciseAudioMutation,
  useResolveExercisePathQuery,
} = exerciseApi;
